import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CodeTableNumber,
  RetrieveCodesMetadataResponse
} from '../interfaces/codeTables';
import {
  decrementActiveCalls,
  incrementActiveCalls
} from '../redux/loading.state';
import { ApiOptions } from './api.interfaces';
import { retrieveMetadata } from './metadata.api';
import { sendDataDeprecated } from './services.api';
import {
  handleFailedApiResponse,
  verifyApiIsConfigured
} from './thunk.helper.helpers';
import {
  CreateGenericThunkParam,
  CreateThunkParams,
  CreateThunkThunkApi
} from './thunk.helper.interface';

/**
 * create async thunk for api calls
 */
export function createThunk<RequestDataType, ResponseType>({
  actionName,
  apiFunc,
  headers,
  method, //may be undefined when coming from MetaData or Services thunk as they are not configurable
  path,
  serviceName,
  useLoadingIndicator
}: CreateThunkParams<RequestDataType, ResponseType>) {
  const apiFuncOptions = {
    headers,
    method,
    path
  };
  const type = actionName || [serviceName, path].join('/');

  return createAsyncThunk<
    ResponseType,
    RequestDataType,
    CreateThunkThunkApi<ResponseType>
  >(
    type,
    (data, { dispatch, rejectWithValue }) => {
      const operation = () => apiFunc(apiFuncOptions, data);

      if (useLoadingIndicator) {
        dispatch(incrementActiveCalls());
      }

      return operation()
        .catch(handleFailedApiResponse(rejectWithValue))
        .finally(() => {
          if (useLoadingIndicator) {
            dispatch(decrementActiveCalls());
          }
        });
    },
    {
      condition: verifyApiIsConfigured,
      dispatchConditionRejection: true
    }
  );
}

/**
 * Create a generic version of `createThunk` where only the action type is provided at instantiation.
 * Additional options used in `createThunk` (e.g. `apiFunc` and `useLoadingIndicator`) are provided at execution.
 */
export function createGenericThunk<RequestDataType = any, ResponseType = any>(
  actionName: string
) {
  return createAsyncThunk<
    ResponseType,
    CreateGenericThunkParam<RequestDataType, ResponseType>,
    CreateThunkThunkApi<ResponseType>
  >(
    actionName,
    (thunkOptions, { dispatch, rejectWithValue }) => {
      const { apiFunc, payload, useLoadingIndicator } = thunkOptions;
      const operation = () => apiFunc(payload);

      if (useLoadingIndicator) {
        dispatch(incrementActiveCalls());
      }

      return operation()
        .catch(handleFailedApiResponse(rejectWithValue))
        .finally(() => {
          if (useLoadingIndicator) {
            dispatch(decrementActiveCalls());
          }
        });
    },
    {
      condition: verifyApiIsConfigured,
      dispatchConditionRejection: true
    }
  );
}

/**
 * create a thunk for core service apis
 */
export function createServicesThunk<RequestDataType, ResponseType>(
  options: ApiOptions
) {
  return createThunk<RequestDataType, ResponseType>({
    apiFunc: sendDataDeprecated,
    serviceName: 'services',
    ...options
  });
}

/**
 * Create a code table-specific version of `createThunk`. It _does not_ increment/decrement the number of
 * active API calls or toggle loading indicators.
 *
 * NOTE: Unlike `createThunk` and `createGenericThunk`, `createCodesThunk` does not attempt to retry a failed
 * API request due to an expired session. This is because metadata API calls do not require the session ID
 * in the header.
 */
export function createCodesThunk<T extends CodeTableNumber>() {
  const type = `codeTables/Code`;
  return createAsyncThunk<
    RetrieveCodesMetadataResponse<T>,
    T,
    CreateThunkThunkApi<any>
  >(
    type,
    async (data, { rejectWithValue }) => {
      return retrieveMetadata<RetrieveCodesMetadataResponse<T>>({
        path: `Codes/Type/${data}`
      }).catch(handleFailedApiResponse(rejectWithValue));
    },
    {
      condition: verifyApiIsConfigured,
      dispatchConditionRejection: true
    }
  );
}
